//done
//@ts-check
import Alpine from 'alpinejs';
import * as config from "../constants.js";
import { gameState } from "./gameState.js";
import { getItemType } from "./inventory.js"
import { findItem } from "./util.js";

/**
 * An item row's searchable description.
 *
 * This was CALLED but never defined — not here, not imported, not on window. So
 * `filteredItems` threw a ReferenceError on any search term that failed to match
 * by name, since the `||` only reaches this when the name misses. Alpine
 * swallows getter throws, so the symptom was a search box that silently stopped
 * narrowing rather than an error anyone would notice.
 */
function getItemDescription(item) {
  const data = item?.artifact || findItem('id', item?.id);
  return data?.description || data?.short || '';
}

/**
 * A plain { itemId: qty } map as the item rows the grid and filters read.
 *
 * Shared by the character's inventory and the layer vaults so all four sources
 * sort, filter and search identically — the vault screens are the same screen
 * pointed somewhere else, not a second implementation of it.
 */
function itemRows(map) {
  const rows = [];
  Object.entries(map || {}).forEach(([id, quantity]) => {
    if (quantity <= 0 || id === 'Fish') return;
    const item = findItem('id', id);
    if (!item) return;
    rows.push({
      id,
      name: item.name,
      quantity,
      type: getItemType(id),
      rarity: item.rarity || 'common',
    });
  });
  return rows;
}

/**
 * The pools each inventory screen shows.
 *
 * Split by WHO PRODUCES the pool, not by which gameState branch stores it.
 * Both outbound flows are made by the same event — a promotion — and the
 * progressionScaling.vaultCarryover is the split between them, so putting them side by side on
 * the Vault page is what makes that upgrade legible. Splitting by branch instead
 * put the consignment next to a dock it cannot be loaded from, which reads as
 * two kinds of Syndicate stock rather than one Command output in transit.
 *
 * The Syndicate then holds exactly one thing: the dock its convoys load.
 */
export const INVENTORY_POOLS = {
  character: [{ key: 'items', label: '🎒 Carried' }],
  commandVault: [
    { key: 'vault', label: '🧑 Outbound to next recruit' },
    { key: 'consignment', label: '⏳ Outbound at next founding' },
  ],
  syndicateStores: [{ key: 'stores', label: '📦 Dock' }],
};

export function createAlpineInventoryStore() {
  // Alpine.js Contractor Store
  Alpine.store("inventory", {
    items: [],
    // The layer vaults. Live on the same store as the character's inventory so
    // one refresh() keeps every screen current — a second store would be a
    // second thing to remember to refresh.
    vault: [],
    stores: [],
    consignment: [],

    init() {
      // Initialize with current gameState data
      this.refresh();
    },

    refresh() {
      this.vault = itemRows(gameState.command?.vault);
      this.stores = itemRows(gameState.syndicate?.stores);
      this.consignment = itemRows(gameState.syndicate?.consignment);

      // Convert gameState.character.inventory to an array format
      const inventoryItems = [];

      if (gameState.character && gameState.character.inventory) {
        // Add regular inventory items
        Object.entries(gameState.character.inventory || {}).forEach(([id, quantity]) => {
          if (quantity > 0 && id != 'Fish') {
            let item = findItem('id', id);
            //remove this later, check based on old name logic
            if (item) {
              inventoryItems.push({
                id: id,
                name: item.name,
                quantity: quantity,
                type: getItemType(id),
                rarity: item.rarity || 'common',
              });
            }
          }
        });

        // Add artifacts
        if (gameState.character.artifacts) {
          gameState.character.artifacts.forEach((artifact) => {
            inventoryItems.push({
              id: artifact.id,
              name: artifact.name,
              quantity: 1,
              type: "artifact",
              rarity: artifact.rarity || 'unique',
              artifact,
            });
          });
        }
      }

      this.items = inventoryItems;
    },

    update(newInventory, newArtifacts) {
      if (newInventory != null) gameState.character.inventory = newInventory;
      if (newArtifacts != null) gameState.character.artifacts = newArtifacts;

      // Refresh from gameState and save
      this.refresh();
    },

    get all() {
      return this.items;
    },

    get count() {
      return this.items.length;
    },
  });
}

/**
 * Alpine.js inventory manager.
 *
 * `source` names an entry in INVENTORY_POOLS, so the Command and Syndicate vault
 * screens are this same component pointed at a different store key — same
 * filters, same sort, same cards. `readOnly` suppresses every action button:
 * nothing in a vault can be sold, used or equipped, because no character owns it.
 */
export function inventoryManager(source = "character", readOnly = false) {
  return {
    searchTerm: "",
    selectedType: "all",
    selectedRarity: "all",
    sortBy: "name",
    source,
    readOnly,
    activePool: (INVENTORY_POOLS[source] || INVENTORY_POOLS.character)[0].key,

    get pools() {
      return INVENTORY_POOLS[this.source] || INVENTORY_POOLS.character;
    },

    setPool(key) {
      this.activePool = key;
    },

    get items() {
      // Use the Alpine store instead of direct gameState access
      return this.$store.inventory[this.activePool] || [];
    },

    get filteredItems() {
      let filtered = this.items;

      // Filter by search term
      if (this.searchTerm) {
        const searchLower = this.searchTerm.toLowerCase();
        filtered = filtered.filter(
          (item) => item.name.toLowerCase().includes(searchLower) || getItemDescription(item).toLowerCase().includes(searchLower)
        );
      }

      // Filter by category
      if (this.selectedType !== "all") {
        filtered = filtered.filter((item) => item.type === this.selectedType);
      }

      // Filter by rarity
      if (this.selectedRarity !== "all") {
        filtered = filtered.filter((item) => item.rarity === this.selectedRarity);
      }

      // Sort items
      filtered.sort((a, b) => {
        switch (this.sortBy) {
          case "name":
            return a.name.localeCompare(b.name);
          case "quantity":
            return b.quantity - a.quantity;
          case "type":
            const typeOrder = { material: 1, techGear: 2, artifact: 3, blueprint: 4, consumable: 5 };
            const typeDiff = (typeOrder[a.type] || 0) - (typeOrder[b.type] || 0);
            return typeDiff !== 0 ? typeDiff : a.name.localeCompare(b.name);
          case "rarity":
            const rarityOrder = { common: 1, uncommon: 2, rare: 3, epic: 4, legendary: 5, unique: 6 };
            const rarityDiff = (rarityOrder[a.rarity] || 0) - (rarityOrder[b.rarity] || 0);
            return rarityDiff !== 0 ? rarityDiff : a.name.localeCompare(b.name);
          case "credits":
            const aCredits = Number(findItem('name',a.name)?.credits || 0);
            const bCredits = Number(findItem('name',b.name)?.credits || 0);
            return bCredits - aCredits; // High to low
          default:
            return 0;
        }
      });

      //console.log("Filtered items:", [...filtered]);

      return filtered;
    },

    get totalItems() {
      return this.items.length;
    },

    get filteredCount() {
      return this.filteredItems.length;
    },

    get itemTypes() {
      const itemTypes = new Set(["all"]);
      this.items.forEach((item) => itemTypes.add(item.type));
      return Array.from(itemTypes).map((itemType) => ({
        value: itemType,
        label:
          itemType === "all" ? "All Items" : config.itemTypes[itemType] ? config.itemTypes[itemType].name : itemType.charAt(0).toUpperCase() + itemType.slice(1),
      }));
    },

    get itemRarities() {
      const rarities = new Set(["all"]);
      this.items.forEach((item) => rarities.add(item.rarity));
      return Array.from(rarities).map((rarity) => ({
        value: rarity,
        label:
          rarity === "all" ? "All Rarities" : config.itemRarities[rarity] ? config.itemRarities[rarity].name : rarity.charAt(0).toUpperCase() + rarity.slice(1),
      }));
    },

    clearFilters() {
      this.searchTerm = "";
      this.selectedType = "all";
      this.selectedRarity = "all";
      this.sortBy = "name";
    },
  };
}

/**
 * The vault screens' markup, built in JS rather than written twice in HTML.
 *
 * The filter bar is ~50 lines of Alpine directives; a third and fourth copy in
 * index.html would drift from the character pane the first time a filter is
 * touched. Injected before Alpine.start() so it is walked like any other markup.
 *
 * @param {string} source - an INVENTORY_POOLS key
 * @param {string} accent - the layer's identity colour, for the pool tabs
 * @param {string} empty  - what to say when the pool is empty
 */
export function inventoryPaneHtml(source, accent, empty) {
  const pools = INVENTORY_POOLS[source] || [];
  // One pool needs no switcher — a single tab that cannot be switched away from
  // is a control that only takes up space.
  const poolTabs = pools.length < 2 ? '' : `
    <div class="flex gap-1 mb-3">
      ${pools.map(p => `
        <button type="button" @click="setPool('${p.key}')"
          class="px-3 py-1.5 rounded text-xs font-bold border-2"
          :style="activePool === '${p.key}'
            ? 'border-color:${accent}; background:${accent}22; color:${accent}'
            : 'border-color:#374151; color:#9ca3af'">
          ${p.label} <span class="opacity-70" x-text="'· ' + ($store.inventory['${p.key}'] || []).length"></span>
        </button>`).join('')}
    </div>`;

  return `
    ${poolTabs}
    <div class="mb-4 space-y-4">
      <div class="flex space-x-2 text-sm">
        <div class="flex-grow">
          <input type="text" x-model="searchTerm" placeholder="Search by name or description..."
            class="w-full px-4 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:border-game-accent focus:outline-none">
        </div>
        <div class="flex-grow">
          <select x-model="selectedType"
            class="w-full px-4 py-[10px] bg-gray-800 border border-gray-600 rounded-lg text-white focus:border-game-accent focus:outline-none">
            <option value="all">All Categories</option>
            <template x-for="[itemTypeKey, itemType] in Object.entries(config.itemTypes).filter(([key]) => key !== 'credits')" :key="itemTypeKey">
              <option :value="itemTypeKey" x-text="\`\${itemType.icon} \${itemType.name}\`"></option>
            </template>
          </select>
        </div>
        <div class="flex-grow">
          <select x-model="selectedRarity"
            class="w-full px-4 py-[10px] bg-gray-800 border border-gray-600 rounded-lg text-white focus:border-game-accent focus:outline-none">
            <template x-for="rarity in itemRarities" :key="rarity.value">
              <option :value="rarity.value" x-text="rarity.label"></option>
            </template>
          </select>
        </div>
        <div class="flex-grow">
          <select x-model="sortBy"
            class="w-full px-4 py-[10px] bg-gray-800 border border-gray-600 rounded-lg text-white focus:border-game-accent focus:outline-none">
            <option value="name">Sort by Name</option>
            <option value="quantity">Sort by Quantity</option>
            <option value="type">Sort by Type</option>
            <option value="rarity">Sort by Rarity</option>
            <option value="credits">Sort by Value</option>
          </select>
        </div>
        <div class="text-center">
          <button @click="clearFilters()"
            class="bg-blue-400 hover:bg-blue-300 text-black text-sm px-4 py-[10px] rounded font-bold whitespace-nowrap">🧹 Clear Filters</button>
        </div>
      </div>
      <div class="text-base text-gray-300 text-center">
        <span x-text="\`Showing \${filteredItems.length} of \${totalItems} items\`"></span>
      </div>
    </div>

    <div class="grid gap-4" style="grid-template-columns: repeat(auto-fit, 11rem); justify-content: center;">
      <template x-for="item in filteredItems" :key="item.name">
        <div x-html="window.createInventoryCard(item, 'inventory', null, null, { collapsed: true, hideActions: true })"></div>
      </template>
      <div x-show="filteredItems.length === 0" class="col-span-full text-center text-gray-400 py-8">
        <span x-show="totalItems === 0">${empty}</span>
        <span x-show="totalItems > 0 && filteredItems.length === 0">No items match your search criteria</span>
      </div>
    </div>`;
}

// Global function to refresh inventory in Alpine store
export function refreshAlpineInventory() {
  // Update the Alpine store with current gameState data
  if (Alpine.store("inventory")) {
    Alpine.store("inventory").refresh();
  }
}
